'use client';

import { useEffect, useCallback } from 'react';
import { ContactForm } from './ContactForm';
import type { ContactFormType } from '../../../types/contact/ContactFormType';

interface ContactFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  formType: ContactFormType;
}

/**
 * @group Contact
 * @summary Dialog wrapper for contact form
 * 
 * Modal dialog that displays the contact form. Separate from existing
 * ContactDialog component (which is for WhatsApp/email links).
 */
export function ContactFormDialog({ isOpen, onClose, formType }: ContactFormDialogProps) {
  const title = formType === 'demo' ? 'Request a Demo' : 'Apply for a Fulltime Job as a Clinician';

  // Handle Escape key to close
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-dialog-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Dialog content */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2
            id="contact-dialog-title"
            className="text-xl font-semibold text-gray-900"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close dialog"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Form */}
        <div className="p-4">
          <ContactForm formType={formType} onSuccess={onClose} />
        </div>
      </div>
    </div>
  );
}
